import { useState } from 'react'
import WarehousesTab from '../admin/production/WarehousesTab'
import SuppliesTab from '../admin/production/SuppliesTab'
import PurchasesTab from '../admin/production/PurchasesTab'
import SelectionTab from '../admin/production/SelectionTab'
import RoastingTab from '../admin/production/RoastingTab'
import BlendingTab from '../admin/production/BlendingTab'
import PackagingTab from '../admin/production/PackagingTab'
import TimelineTab from '../admin/production/TimelineTab'
import SuppliersTab from '../collaborator/logistics/SuppliersTab'
import QuotesTab from '../collaborator/logistics/QuotesTab'
import ServicesTab from '../collaborator/logistics/ServicesTab'

type TabKey = 'inventory' | 'supply_chain' | 'suppliers' | 'quotes' | 'services'

interface Tab {
  key: TabKey
  label: string
}

const tabs: Tab[] = [
  { key: 'inventory', label: 'Inventario' },
  { key: 'supply_chain', label: 'Cadena de suministro' },
  { key: 'suppliers', label: 'Proveedores' },
  { key: 'quotes', label: 'Cotizaciones' },
  { key: 'services', label: 'Servicios' },
]

type InventorySubTabKey = 'warehouses' | 'supplies'

const inventorySubTabs: { key: InventorySubTabKey; label: string }[] = [
  { key: 'warehouses', label: 'Almacenes' },
  { key: 'supplies', label: 'Insumos' },
]

type SupplyChainSubTabKey = 'purchases' | 'selection' | 'roasting' | 'blending' | 'packaging' | 'timeline'

const supplyChainSubTabs: { key: SupplyChainSubTabKey; label: string }[] = [
  { key: 'purchases', label: 'Compras' },
  { key: 'selection', label: 'Selección' },
  { key: 'roasting', label: 'Tostado' },
  { key: 'blending', label: 'Mezclado' },
  { key: 'packaging', label: 'Empaquetado' },
  { key: 'timeline', label: 'Línea de Tiempo' },
]

function SubTabStrip<K extends string>({
  ariaLabel,
  subTabs,
  active,
  onChange,
}: {
  ariaLabel: string
  subTabs: { key: K; label: string }[]
  active: K
  onChange: (key: K) => void
}) {
  return (
    <div role="tablist" aria-label={ariaLabel} className="flex flex-wrap gap-1 border-b border-espresso-200 mb-4">
      {subTabs.map((tab) => (
        <button
          key={tab.key}
          role="tab"
          aria-selected={active === tab.key}
          onClick={() => onChange(tab.key)}
          className={`px-3 py-1.5 text-sm font-medium rounded-t-md border-b-2 -mb-px focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta-500 ${
            active === tab.key
              ? 'border-terracotta-600 text-terracotta-700 bg-terracotta-50'
              : 'border-transparent text-espresso-500 hover:text-espresso-800 hover:bg-cream-100'
          }`}
        >
          {tab.label}
        </button>
      ))}
    </div>
  )
}

/**
 * Shared Logística page: renders the same 5-tab structure (Inventario, Cadena
 * de suministro, Proveedores, Cotizaciones, Servicios) for both the admin
 * route (`/admin/production`) and the `logistica` collaborator panel route
 * (`/logistica`). The backend already grants both roles full edit access via
 * `@require_panel("logistica")`, so there is intentionally only one
 * implementation of this page.
 */
export default function LogisticaPage() {
  const [activeTab, setActiveTab] = useState<TabKey>('inventory')
  const [inventorySubTab, setInventorySubTab] = useState<InventorySubTabKey>('warehouses')
  const [supplyChainSubTab, setSupplyChainSubTab] = useState<SupplyChainSubTabKey>('purchases')

  return (
    <div>
      <h1 className="text-2xl font-bold text-espresso-800 mb-2">Logística</h1>
      <p className="text-sm text-espresso-500 mb-6">
        Administra inventario, la cadena de suministro de producción, proveedores, cotizaciones y servicios
        contratados.
      </p>

      <div role="tablist" aria-label="Secciones de logística" className="flex flex-wrap gap-1 border-b border-espresso-200 mb-6">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            role="tab"
            aria-selected={activeTab === tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2 text-sm font-medium rounded-t-md border-b-2 -mb-px focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta-500 ${
              activeTab === tab.key
                ? 'border-terracotta-600 text-terracotta-700 bg-terracotta-50'
                : 'border-transparent text-espresso-500 hover:text-espresso-800 hover:bg-cream-100'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div role="tabpanel">
        {activeTab === 'inventory' && (
          <div>
            <SubTabStrip
              ariaLabel="Secciones de inventario"
              subTabs={inventorySubTabs}
              active={inventorySubTab}
              onChange={setInventorySubTab}
            />
            {inventorySubTab === 'warehouses' && <WarehousesTab />}
            {inventorySubTab === 'supplies' && <SuppliesTab />}
          </div>
        )}

        {activeTab === 'supply_chain' && (
          <div>
            <SubTabStrip
              ariaLabel="Etapas de la cadena de suministro"
              subTabs={supplyChainSubTabs}
              active={supplyChainSubTab}
              onChange={setSupplyChainSubTab}
            />
            {supplyChainSubTab === 'purchases' && <PurchasesTab />}
            {supplyChainSubTab === 'selection' && <SelectionTab />}
            {supplyChainSubTab === 'roasting' && <RoastingTab />}
            {supplyChainSubTab === 'blending' && <BlendingTab />}
            {supplyChainSubTab === 'packaging' && <PackagingTab />}
            {supplyChainSubTab === 'timeline' && <TimelineTab />}
          </div>
        )}

        {activeTab === 'suppliers' && <SuppliersTab />}
        {activeTab === 'quotes' && <QuotesTab />}
        {activeTab === 'services' && <ServicesTab />}
      </div>
    </div>
  )
}
